import { useCallback, useEffect } from 'react';
import { useEventFeedStore } from '@/stores/event-feed.store';

interface UsePlayerStateReporterOptions {
    isLive: boolean;
    playingEventId?: string | null;
}

interface UsePlayerStateReporterResult {
    onTimeUpdate: (currentTime: number) => void;
    onPlayingChange: (isPlaying: boolean) => void;
    onLoopChange: (loopEnabled: boolean) => void;
    onSeeked: () => void;
}

export function usePlayerStateReporter({
    isLive,
    playingEventId,
}: UsePlayerStateReporterOptions): UsePlayerStateReporterResult {
    const setPlayerCurrentTime = useEventFeedStore(
        (state) => state.setPlayerCurrentTime,
    );
    const setPlayerIsLive = useEventFeedStore((state) => state.setPlayerIsLive);
    const setPlayerLoopEnabled = useEventFeedStore(
        (state) => state.setPlayerLoopEnabled,
    );
    const setPlayerIsPaused = useEventFeedStore(
        (state) => state.setPlayerIsPaused,
    );
    const setPlayerHasSeeked = useEventFeedStore(
        (state) => state.setPlayerHasSeeked,
    );
    const resetPlayerState = useEventFeedStore(
        (state) => state.resetPlayerState,
    );

    useEffect(() => {
        setPlayerIsLive(isLive);
    }, [isLive, setPlayerIsLive]);

    useEffect(() => {
        setPlayerCurrentTime(null);
        setPlayerHasSeeked(false);
    }, [playingEventId, setPlayerCurrentTime, setPlayerHasSeeked]);

    useEffect(() => () => resetPlayerState(), [resetPlayerState]);

    const onTimeUpdate = useCallback(
        (currentTime: number) => setPlayerCurrentTime(currentTime),
        [setPlayerCurrentTime],
    );

    const onPlayingChange = useCallback(
        (isPlaying: boolean) => setPlayerIsPaused(!isPlaying),
        [setPlayerIsPaused],
    );

    const onLoopChange = useCallback(
        (loopEnabled: boolean) => setPlayerLoopEnabled(loopEnabled),
        [setPlayerLoopEnabled],
    );

    const onSeeked = useCallback(
        () => setPlayerHasSeeked(true),
        [setPlayerHasSeeked],
    );

    return { onTimeUpdate, onPlayingChange, onLoopChange, onSeeked };
}
